import { useIsAuthenticated } from '@azure/msal-react'
import { jwtDecode } from 'jwt-decode'
import { useEffect, useState } from 'react'

import { acquireAccessToken } from '../config/msalInstanceHelper'

export interface AuthState {
  isAuthenticated: boolean
  hasAnyRole: boolean
  roles: string[]
  loading: boolean
}

interface JwtPayload {
  roles?: string[]
  [key: string]: unknown
}

const REQUIRED_ROLES = [
  'Products.Read',
  'Configs.Write',
  'Products.Write',
  'Admin.Write',
  'Storyboards.Write',
  'Admin.Read',
  'Configs.Read',
  'Storyboards.Read',
]

export function useAuth(): AuthState {
  const isAuthenticated = useIsAuthenticated()
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    hasAnyRole: false,
    roles: [],
    loading: true,
  })

  useEffect(() => {
    if (!isAuthenticated) {
      setAuthState({
        isAuthenticated: false,
        hasAnyRole: false,
        roles: [],
        loading: false,
      })
      return
    }

    // Acquire access token via MSAL API, then decode roles from it
    const checkRoles = async () => {
      try {
        const token = await acquireAccessToken()

        if (!token) {
          console.warn('[useAuth] No access token returned')
          setAuthState({ isAuthenticated: true, hasAnyRole: false, roles: [], loading: false })
          return
        }

        const decoded = jwtDecode<JwtPayload>(token)
        const userRoles: string[] = decoded['roles'] || []

        const hasAnyRole = REQUIRED_ROLES.some((requiredRole) => userRoles.includes(requiredRole))

        setAuthState({
          isAuthenticated: true,
          hasAnyRole,
          roles: userRoles,
          loading: false,
        })
      } catch (error) {
        console.warn('[useAuth] Failed to acquire token or check roles:', error)
        setAuthState({
          isAuthenticated: true,
          hasAnyRole: false,
          roles: [],
          loading: false,
        })
      }
    }

    checkRoles()
  }, [isAuthenticated])

  return authState
}

export function hasSpecificRoles(userRoles: string[], requiredRoles: string[]): boolean {
  return requiredRoles.some((role) => userRoles.includes(role))
}
